import React, { useState, useEffect } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  Typography,
  Radio,
  FormControlLabel,
  CircularProgress,
  RadioGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton
} from '@material-ui/core';
import {
  setConfirmationModalState
} from 'store/actions';
import SearchIcon from '@material-ui/icons/Search';
import { Constants } from 'utilities/constants';
import ClearIcon from '@material-ui/icons/Clear';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { startOfDay, startOfToday, endOfToday, addDays } from 'date-fns';
import { format, parseISO } from 'date-fns'

const EditModal = ({
  module,
  isOpen,
  classes,
  selectedRows,
  editField,
  editTasks,
  setIsOpen,
  setConfirmationModalState
}) => {
  const [newValue, setNewValue] = useState(null);
  const [hasError, setHasError] = useState(false);
  const lengthOutsideRange = newValue?.length < 3 || newValue?.length > 100;

  useEffect(() => {
      if (isOpen) {
        setNewValue(null);
      }
    }, [isOpen]); 
  
  if (!editField) return null;

  function handleEdit() {
    const rowsToUpdate = selectedRows.filter(row => getFormattedValue(row[editField.field]) !== getFormattedValue(newValue));
    const hasRowsMessage = `${editField.displayName} for ${rowsToUpdate.length} task${rowsToUpdate.length > 1 ? 's' : ''} will be updated to ${getFormattedValue(newValue, true)}. Continue?`;
    const hasNoRowsMessage = 'No changes will be made. Continue?'
    setConfirmationModalState({ // Open confirmation modal
      isModalOpen: true,
      handleConfirmation: () => {
        closeConfirmationModal();        
        editTasks(isPostAcq ? 'postAcqTask' : 'txnOpp', rowsToUpdate, newValue);
      },
      handleCancellation: closeConfirmationModal,
      titleText: 'Confirm',
      contentText: rowsToUpdate.length ? hasRowsMessage : hasNoRowsMessage
    });
  }

  const closeConfirmationModal = () => {
      setConfirmationModalState({ isModalOpen: false });
    }

  const handleClose = (e, reason) => {
    if (reason === 'backdropClick') return; // updated handling for disableBackdropClick
    setIsOpen(false);
  }

  const handleTextFieldChange = e => {
    setNewValue(e.target.value === '' ? null : e.target.value);
  }

  const handleDatePickerError = e => {
    if (!e && hasError) {
      setHasError(false);
      return;
    } else if (e && !hasError) {
      setHasError(true);
    }
  }

  const getEditorType = () => {
    if (!selectedRows.length) return null;
    if (!editField) return <Typography>Failed to find config for field</Typography>
    
    switch (editField.type) {
      case 'date':
        return (
          <KeyboardDatePicker
            // placeholder={allSameValue ? selectedRows[0][editField.field] : Date.now()}
            variant='inline'
            value={newValue}
            onChange={date => setNewValue(startOfDay(date))}
            format="MM/dd/yyyy"
            openTo="date"
            minDate={startOfToday()}
            maxDate={addDays(endOfToday(), 30)}
            disableToolbar
            style={{ marginTop: -6 }}
            onError={handleDatePickerError}
          />
        )
      case 'string':
        return (
          <TextField
            value={newValue ?? ''}
            onChange={handleTextFieldChange}
            autoFocus
            style={{ marginTop: -6, width: '100%' }}
            error={lengthOutsideRange}
            helperText={lengthOutsideRange ? 'List Name must be between 3-100 characters' : ''}
          />
        )
      default:
        return <Typography>Failed to find data type for field {editField.displayName}</Typography>
    }
  }

  const groupedValues = selectedRows.reduce((acc, row) => {
    const value = row[editField.field];
    acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {});

  const getFormattedValue = (value, isConfirmationModal) => {
    if (!value) return value;
    switch (editField.type) {
      case 'date':
        if (value instanceof Date && !isNaN(value)) {
          return format(value, 'MM/dd/yyyy');
        }
        const parsed = parseISO(value);
        if (parsed instanceof Date && !isNaN(parsed)){
          return format(parsed, 'MM/dd/yyyy');
        }
        return value;
      case 'string':
        if (isConfirmationModal) return `"${value}"`;
        return value;
      default:
        return value;
    }
  }

  const isPostAcq = module === Constants.Module.POSTACQ
  const selectedMessage = isPostAcq
    ? `${selectedRows.length} task${selectedRows.length > 1 ? 's' : ''} selected`
    : `${selectedRows.length} opportunit${selectedRows.length > 1 ? 'ies' : 'y'} selected`
  return (
    <Dialog
      open={isOpen} onClose={handleClose} maxWidth='sm' fullWidth classes={{ paper: classes.assignDialog }}>
      <DialogTitle disableTypography={true} className={classes.dialogTitleRoot}>
        <Typography variant={'h6'}>
          <span>{`Edit ${isPostAcq ? 'Tasks' : 'Transaction Opportunities'} - ${editField.displayName}`}</span>
        </Typography>
        <Typography variant='body1' >{selectedMessage}</Typography>
      </DialogTitle>
      <IconButton className={classes.closeButton} onClick={handleClose}>
        <ClearIcon fontSize="inherit" />
      </IconButton>
      <DialogContent>
        <Typography variant='subtitle2'>{`Current value${Object.keys(groupedValues).length > 1 ? 's' : ''}:`}</Typography>
        <div className={classes.currentValuesContainer}>
          {Object.keys(groupedValues).sort((a, b) => groupedValues[b] - groupedValues[a]).map(key => {
            return <Typography key={key}>{`${groupedValues[key]} row${groupedValues[key] > 1 ? 's' : ''}: ${getFormattedValue(key)}`}</Typography>
          })}
        </div>
        <Typography variant='subtitle2' style={{ marginTop: 16 }}>New value:</Typography>
        {getEditorType()}
      </DialogContent>
      <DialogActions>
        <Button variant='contained' onClick={handleClose}>Cancel</Button>
        <Button variant='contained' color='primary' onClick={() => handleEdit()} disabled={lengthOutsideRange || hasError}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

const styles = theme => ({
  assignDialog: {
    // height: '50%',
    minHeight: 350
  },
  loadingWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%'
  },
  filterField: {
    marginTop: 16
  },
  dialogTitleRoot: {
    paddingBottom: 0
  },
  closeButton: {
    position: 'absolute',
    right: 0,
    top: 0,
    backgroundColor: '#ffffff'
  },
  currentValuesContainer: {
    backgroundColor: '#dddddd',
    borderRadius: 4,
    padding: '4px 8px',
    maxHeight: 205,
    overflow: 'auto'
  }
});

const mapState = state => {
  return {

  }
};

const mapDispatch = {
  setConfirmationModalState
};

export default compose(
  withStyles(styles),
  connect(mapState, mapDispatch)
)(EditModal);